import React, { useMemo, Children, useState } from 'react'
import PropTypes from 'prop-types'
import { keyBy, capitalize, cloneDeep } from 'lodash'

import { useFormContext, useWatch } from 'react-hook-form'
import { Box, Typography, Button, Stack } from '@mui/material'

import { get } from 'utils/lodash'
import { calculateSum, formatCentsToCurrency } from 'utils/price'
import { getTaxes } from 'components/customer-details/content/work-orders/actions/disposal-ticket/utils'

import T from 'T'
import CancelButton from 'components/buttons/CancelButton'

import Preview from './Preview'
import PreviewField from './fields/PreviewField'
import { getDisposalSiteDetails, getDisposalFeeDetails } from './utils'

const DisposalFee = ({ serviceData = {}, allSites = [] }) => {
  const [editMode, setEditMode] = useState(null)
  const { control, watch, setValue } = useFormContext()
  const materials = useWatch({ control, name: 'materials' })
  const hasValidMaterials = useMemo(() => materials.some(material => material.disposalSiteMaterialId), [materials])
  const taxable = watch('taxable')
  const disposalSiteId = watch('disposalSiteId')
  const disposalSiteDetails = useMemo(() => getDisposalSiteDetails(disposalSiteId, allSites), [disposalSiteId, allSites])
  const disposalSiteMaterials = useMemo(() => get(disposalSiteDetails, 'disposalSiteMaterial', []), [disposalSiteDetails])
  const disposalSiteMaterialsKeyBy = useMemo(() => keyBy(disposalSiteMaterials, 'id'), [disposalSiteMaterials])
  const validMaterials = materials.filter(material => material.disposalSiteMaterialId)
  const allTaxes = useMemo(() => (taxable ? getTaxes(serviceData) : []), [serviceData, taxable])
  const materialDetails = getDisposalFeeDetails(validMaterials, allTaxes, disposalSiteMaterialsKeyBy)
  const totalCents = useMemo(() => calculateSum(materialDetails, 'disposalFeeData.subtotalCentsWithTax'), [materialDetails])

  const handleOpenEditDisposalFee = () => setEditMode(cloneDeep(materials))

  const handleCloseEditDisposalFee = () => setEditMode(null)

  const handleCancelEditDisposalFee = () => {
    setValue('materials', editMode)
    handleCloseEditDisposalFee()
  }

  return (
    <>
      <Box border="1px solid" borderColor="divider" borderRadius={1} p={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={500}>
            {capitalize(T.DISPOSAL_FEE)}
          </Typography>
          {hasValidMaterials && (
            <>
              {editMode ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CancelButton onClick={handleCancelEditDisposalFee} />
                  <Button size="small" variant="outlined" onClick={handleCloseEditDisposalFee}>
                    {T.SAVE}
                  </Button>
                </Box>
              ) : (
                <Button size="small" variant="outlined" onClick={handleOpenEditDisposalFee}>
                  {T.EDIT}
                </Button>
              )}
            </>
          )}
        </Box>
        <Stack direction="column" gap={0.5}>
          {Children.toArray(materialDetails.map((material, index) => <Preview index={index} isEditable={editMode} material={material} />))}
        </Stack>
        <PreviewField
          mt={1}
          color="primary.main"
          labelLeft="Total:"
          labelRight={formatCentsToCurrency(totalCents)}
          leftProps={{ variant: 'h5' }}
          rightProps={{ variant: 'h5' }}
        />
      </Box>
    </>
  )
}

DisposalFee.propTypes = {
  serviceData: PropTypes.object,
  allSites: PropTypes.array,
}

export default DisposalFee
